import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import {
  IconArrowsExchange,
  IconLoader2,
  IconSearch,
} from '@tabler/icons-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import novaOpsApi from '@/lib/novaOpsApi';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';

type Box = {
  uuid: string;
  label: string;
  simsInScope: number;
  byStatus: Record<string, number>;
  partnerCount: number;
  partnerNames: string[];
  simInventoryOwnerPartnerCount: number;
  consignmentCount: number;
};

type Partner = { uuid: string; name: string };

const errorMessage = (error: any, fallback: string) =>
  error?.response?.data?.error?.message ??
  error?.response?.data?.message ??
  error?.message ??
  fallback;

const isEligible = (box: Box) =>
  box.simsInScope > 0 &&
  box.byStatus.ALLOCATED === box.simsInScope &&
  box.partnerCount === 1 &&
  box.simInventoryOwnerPartnerCount === 0 &&
  box.consignmentCount === 0;

export function InnerboxPage() {
  const [innerboxNumber, setInnerboxNumber] = useState('');
  const [box, setBox] = useState<Box | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState<'reassign' | 'unassign'>('reassign');
  const [partnerSearch, setPartnerSearch] = useState('');
  const [partners, setPartners] = useState<Partner[]>([]);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [reason, setReason] = useState('');
  const partnerSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (partnerSearchTimer.current) clearTimeout(partnerSearchTimer.current);
  }, []);

  const searchBox = async () => {
    const label = innerboxNumber.trim();
    if (!label) return;
    setLoading(true);
    setError('');
    setSuccess('');
    setBox(null);
    try {
      const response = await novaOpsApi.get<Box[]>('/innerboxes', {
        params: { label, page: 1, limit: 20, sortBy: 'label', sortOrder: 'ASC' },
      });
      const match = response.data.find(
        (candidate) => candidate.label.toLowerCase() === label.toLowerCase(),
      );
      if (!match) {
        setError(`No innerbox with label “${label}” was found in Nova.`);
        return;
      }
      setBox(match);
    } catch (err) {
      setError(errorMessage(err, 'Failed to search Nova inventory.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    void searchBox();
  };

  const loadPartners = (value: string) => {
    if (partnerSearchTimer.current) clearTimeout(partnerSearchTimer.current);
    setPartnerSearch(value);
    setSelectedPartner(null);
    partnerSearchTimer.current = setTimeout(async () => {
      if (value.trim().length < 2) {
        setPartners([]);
        return;
      }
      setPartnerLoading(true);
      try {
        const response = await novaOpsApi.get<Partner[]>('/innerboxes/partners', {
          params: { search: value.trim() },
        });
        setPartners(response.data);
      } catch (err) {
        setError(errorMessage(err, 'Failed to search eligible partners.'));
        setPartners([]);
      } finally {
        setPartnerLoading(false);
      }
    }, 300);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setPartnerSearch('');
    setPartners([]);
    setSelectedPartner(null);
    setReason('');
  };

  const reassign = async () => {
    if (!box || !selectedPartner || !reason.trim()) return;
    setLoading(true);
    setError('');
    try {
      const response = await novaOpsApi.post<{
        boxLabel: string;
        previousPartnerName: string;
        targetPartnerName: string;
        reassignedCount: number;
      }>(`/innerboxes/${box.uuid}/reassign`, {
        targetPartnerId: selectedPartner.uuid,
        reason: reason.trim(),
      });
      setSuccess(
        `Reassigned ${response.data.reassignedCount} SIMs in ${response.data.boxLabel} from ${response.data.previousPartnerName} to ${response.data.targetPartnerName}.`,
      );
      closeDialog();
      setBox((current) =>
        current
          ? { ...current, partnerNames: [response.data.targetPartnerName] }
          : current,
      );
    } catch (err) {
      setError(errorMessage(err, 'Innerbox reassignment failed.'));
    } finally {
      setLoading(false);
    }
  };

  const unassign = async () => {
    if (!box || !reason.trim()) return;
    setLoading(true);
    setError('');
    try {
      const response = await novaOpsApi.post<{
        boxLabel: string;
        previousPartnerName: string;
        unassignedCount: number;
      }>(`/innerboxes/${box.uuid}/unassign`, { reason: reason.trim() });
      setSuccess(
        `Returned ${response.data.unassignedCount} SIMs in ${response.data.boxLabel} from ${response.data.previousPartnerName} to MVNO inventory.`,
      );
      closeDialog();
      setBox((current) =>
        current
          ? {
              ...current,
              byStatus: { INVENTORY: current.simsInScope },
              partnerCount: 0,
              partnerNames: [],
            }
          : current,
      );
    } catch (err) {
      setError(errorMessage(err, 'Innerbox unassignment failed.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Innerbox Management"
        subtitle="Search Nova inventory and correct eligible box assignments"
      />

      <MainCard className="mb-6">
        <form onSubmit={handleSearch} className="space-y-3">
          <div>
            <Label htmlFor="innerbox-number">Innerbox label</Label>
            <Input
              id="innerbox-number"
              value={innerboxNumber}
              onChange={(event) => setInnerboxNumber(event.target.value)}
              placeholder="e.g. MA-0055-P01-O93-I25"
              required
            />
            <p className="mt-1 text-xs text-muted-foreground">
              This searches the Nova PostgreSQL SIM-box inventory.
            </p>
          </div>

          {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
          {success && <Alert><AlertDescription>{success}</AlertDescription></Alert>}

          <div className="flex gap-3">
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? <IconLoader2 size={16} className="animate-spin" /> : <IconSearch size={16} />}
              {loading ? 'Searching...' : 'Search Innerbox'}
            </Button>
            {box && (
              <Button
                type="button"
                variant="outline"
                onClick={() => { setDialogAction('reassign'); setDialogOpen(true); }}
                disabled={!isEligible(box) || loading}
              >
                <IconArrowsExchange size={16} /> Reassign
              </Button>
            )}
            {box && (
              <Button
                type="button"
                variant="destructive"
                onClick={() => { setDialogAction('unassign'); setDialogOpen(true); }}
                disabled={!isEligible(box) || loading}
              >
                Unassign to MVNO
              </Button>
            )}
          </div>
        </form>
      </MainCard>

      {box && (
        <MainCard>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="text-lg font-semibold">{box.label}</h3>
              <p className="text-sm text-muted-foreground">
                Current partner: {box.partnerNames.join(', ') || 'Unassigned'}
              </p>
            </div>
            {!isEligible(box) && (
              <p className="text-sm text-destructive">
                This box cannot be reassigned. It must contain only allocated, unconsigned stock owned by one partner.
              </p>
            )}
          </div>
          <Table>
            <TableHeader><TableRow><TableHead>SIMs</TableHead><TableHead>Allocated</TableHead><TableHead>Other statuses</TableHead><TableHead>Consigned</TableHead></TableRow></TableHeader>
            <TableBody><TableRow><TableCell>{box.simsInScope}</TableCell><TableCell>{box.byStatus.ALLOCATED ?? 0}</TableCell><TableCell>{Object.entries(box.byStatus).filter(([status]) => status !== 'ALLOCATED').map(([status, count]) => `${status}: ${count}`).join(', ') || 'None'}</TableCell><TableCell>{box.consignmentCount}</TableCell></TableRow></TableBody>
          </Table>
        </MainCard>
      )}

      <Dialog open={dialogOpen} onOpenChange={(open) => (open ? setDialogOpen(true) : closeDialog())}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{dialogAction === 'reassign' ? 'Reassign' : 'Unassign'} {box?.label}</DialogTitle></DialogHeader>
          <Alert><AlertDescription>{dialogAction === 'reassign' ? 'This changes only eligible, pre-account SIM allocation. Nova rechecks eligibility when you confirm.' : 'This returns the whole eligible box to MVNO inventory. It does not transfer stock to a branch inventory pool.'}</AlertDescription></Alert>
          <div className="space-y-4">
            {dialogAction === 'reassign' && <div>
              <Label htmlFor="partner-search">New leaf partner</Label>
              <Input id="partner-search" value={partnerSearch} onChange={(event) => loadPartners(event.target.value)} placeholder="Type at least 2 characters" />
              {partnerLoading && <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><IconLoader2 size={12} className="animate-spin" /> Searching...</p>}
              {partners.length > 0 && <div className="mt-1 max-h-40 overflow-y-auto rounded-md border">{partners.map((partner) => <button key={partner.uuid} type="button" className="block w-full px-3 py-2 text-left text-sm hover:bg-muted" onClick={() => { setSelectedPartner(partner); setPartnerSearch(partner.name); setPartners([]); }}>{partner.name}</button>)}</div>}
              {selectedPartner && <p className="mt-1 text-xs text-muted-foreground">Selected: {selectedPartner.name}</p>}
            </div>}
            <div>
              <Label htmlFor="reason">Reason</Label>
              <Input id="reason" value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Why is this stock correction required?" maxLength={1000} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button variant={dialogAction === 'unassign' ? 'destructive' : 'default'} onClick={() => void (dialogAction === 'reassign' ? reassign() : unassign())} disabled={(dialogAction === 'reassign' && !selectedPartner) || !reason.trim() || loading}>
              {loading ? <IconLoader2 size={16} className="animate-spin" /> : dialogAction === 'reassign' ? <IconArrowsExchange size={16} /> : null} {dialogAction === 'reassign' ? 'Reassign' : 'Unassign to MVNO'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
